import * as THREE from 'three';

import { deriveNaturalLessThanTrace } from '../../model/natural/comparison';
import { projectNaturalLessThanBooleanChannel } from './lessThanTraceProjection';
import { momentKey, momentPart, withMomentFrame } from '../primitiveMoment';
import type { MomentPart } from '@/_contract/_~generated/directionMomentSchema';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '../../../_contract/_~generated/operationDirectionEffect';
import type { Config, PrimitiveWheelRole, PrimitiveWheelState } from '../../../_contract/config';
import type { PrimitiveKeyframe, PrimitiveStepState } from '../../../_contract/steps';

/** Embed the channel across an arithmetic rod, with Source left when facing Decision. */
export function projectEmbeddedNaturalComparison(args: {
    config: Config;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    source: number;
    target: number;
    sourceRole: PrimitiveWheelRole;
    targetRole: PrimitiveWheelRole;
    sourceVariant?: PrimitiveWheelState['variant'];
    targetVariant?: PrimitiveWheelState['variant'];
    resting: readonly PrimitiveWheelState[];
    frame: MomentPart;
    view: string;
}): PrimitiveStepState[] {
    const { config } = args;
    const isSource = (wheel: PrimitiveWheelState) => wheel.role === args.sourceRole && wheel.variant === args.sourceVariant;
    const isTarget = (wheel: PrimitiveWheelState) => wheel.role === args.targetRole && wheel.variant === args.targetVariant;
    const sourceWheel = args.resting.find(isSource);
    const targetWheel = args.resting.find(isTarget);
    if (!sourceWheel || !targetWheel || sourceWheel.position[2] === targetWheel.position[2]) {
        throw new Error('Embedded comparison requires two distinct axial operand stations.');
    }
    const sign = Math.sign(sourceWheel.position[2] - targetWheel.position[2]);
    const midpoint = (sourceWheel.position[2] + targetWheel.position[2]) / 2;
    const maximumPower = Math.max(...sourceWheel.powers, ...targetWheel.powers);
    const radius = config.base.display.wheel.innerRadius + maximumPower
        * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap)
        + config.base.display.wheel.ringWidth;
    // Spread wheels at least one full radius apart so they don't visually overlap when viewed face-on.
    const halfNatural = Math.abs(sourceWheel.position[2] - targetWheel.position[2]) / 2;
    const half = Math.max(halfNatural, radius + config.base.display.wheel.wheelGap / 24);
    const depth = Math.max(config.boolean.display.layout.resultChannelDepth,
        config.base.display.wheel.wheelGap * 2, radius * 2 + config.base.display.wheel.wheelGap);
    const turn = new THREE.Matrix4().makeRotationY(sign * Math.PI / 2);
    const point = ([x, y, z]: readonly number[]): [number, number, number] =>
        [sign * z, y, midpoint - sign * x];
    const direction = ([x, y, z]: readonly number[]): [number, number, number] => [sign * z, y, -sign * x];
    const unwrap = (angle: number, previous: number) =>
        angle + Math.round((previous - angle) / (2 * Math.PI)) * 2 * Math.PI;
    const rotations = new Map<PrimitiveWheelRole, [number, number, number]>();
    const projected: PrimitiveStepState[] = projectNaturalLessThanBooleanChannel({
        config, source: args.source, target: args.target, directionCatalog: args.directionCatalog,
        positions: { inputOne: [-half, 0, 0], inputTwo: [half, 0, 0], output: [0, 0, -depth] },
        trace: deriveNaturalLessThanTrace({ source: args.source, target: args.target, radix: config.natural.radix }),
    }).filter(step => step.stepKey !== 'complete').map(step => ({
        ...withMomentFrame(step, args.frame),
        cameraFocus: { stationKey: args.view, viewKey: args.view,
            roles: [args.sourceRole, args.targetRole, 'less-than-decision' as const] },
        keyframes: step.keyframes.map(frame => ({
            ...frame,
            wheels: [
                ...args.resting.filter(wheel => !isSource(wheel) && !isTarget(wheel)),
                ...frame.wheels.map(wheel => {
                    const euler = new THREE.Euler().setFromRotationMatrix(turn.clone().multiply(
                        new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(...wheel.wheelRotation ?? [Math.PI / 2, 0, 0]))));
                    const previous = rotations.get(wheel.role);
                    const rotation: [number, number, number] = previous
                        ? [unwrap(euler.x, previous[0]), unwrap(euler.y, previous[1]), unwrap(euler.z, previous[2])]
                        : [euler.x, euler.y, euler.z];
                    rotations.set(wheel.role, rotation);
                    return { ...wheel,
                        role: wheel.role === 'less-than-source' ? args.sourceRole
                            : wheel.role === 'less-than-target' ? args.targetRole : wheel.role,
                        ...(wheel.role === 'less-than-source' ? { variant: args.sourceVariant }
                            : wheel.role === 'less-than-target' ? { variant: args.targetVariant } : {}),
                        ...(wheel.role === 'less-than-source' ? { helperWheel: sourceWheel.helperWheel }
                            : wheel.role === 'less-than-target' ? { helperWheel: targetWheel.helperWheel } : {}),
                        position: point(wheel.position), wheelRotation: rotation,
                    };
                }),
            ],
            booleanRod: frame.booleanRod && { segments: frame.booleanRod.segments.map(segment => ({
                ...segment, from: point(segment.from), to: point(segment.to),
                ...(segment.profile ? { profile: { ...segment.profile, lateral: direction(segment.profile.lateral) } } : {}),
            })) },
        })),
    }));
    const entry = projected[0];
    const first = entry.keyframes[0];
    const decision = first.wheels.find(wheel => wheel.role === 'less-than-decision')!;
    const channel = { segments: first.booleanRod!.segments.filter(segment =>
        !segment.segmentKey.includes('input-') && !segment.segmentKey.startsWith('traveller:')) };
    const hideInputs = (rod: NonNullable<PrimitiveKeyframe['booleanRod']>) => ({
        segments: rod.segments.map(segment => segment.segmentKey.includes('input-') || segment.segmentKey.startsWith('traveller:')
            ? { ...segment, opacity: 0 } : segment),
    });
    const resting = args.resting.map(wheel => {
        if (!isSource(wheel) && !isTarget(wheel)) return wheel;
        const operand = first.wheels.find(candidate => candidate.role === wheel.role && candidate.variant === wheel.variant)!;
        return { ...wheel, powers: operand.powers, wheelRotation: [Math.PI / 2, 0, 0] as [number, number, number],
            bandOpacityByPower: Object.fromEntries(operand.powers.map(power => [
                power, wheel.powers.includes(power) ? wheel.bandOpacityByPower?.[power] ?? 1 : 0,
            ])) };
    });
    const durationMs = config.natural.animation?.naturalLessThan?.helperEntryDurationMs ?? 0;
    entry.keyframes = [
        { wheels: [...resting, decision], booleanRod: hideInputs(first.booleanRod!), transition: 'instant', events: [] },
        { ...first, transition: { durationMs } },
    ];
    const last = projected.at(-1)!.keyframes.at(-1)!;
    const resolved = last.wheels.find(wheel => wheel.role === 'less-than-decision')!;
    const faded = hideInputs(last.booleanRod!);
    const returned: PrimitiveKeyframe = {
        wheels: [...resting, resolved], booleanRod: faded, transition: { durationMs }, events: [],
    };
    // Return from a neutral comparison pose, then undo the entry quarter-turn exactly.
    const neutral = { ...first, booleanRod: faded, wheels: first.wheels.map(wheel =>
        wheel.role === 'less-than-decision' ? resolved : wheel), transition: { durationMs } };
    projected.push({
        ...momentKey({ frames: [args.frame], step: momentPart('return') }), label: 'Return from Comparison',
        description: `${args.source} < ${args.target} is ${args.source < args.target}; return both wheels to the arithmetic rod.`,
        cameraFocus: entry.cameraFocus,
        keyframes: [
            { ...last, transition: 'instant' },
            neutral,
            returned,
            { ...returned, booleanRod: channel, transition: 'instant', wheels: [...args.resting, resolved] },
        ],
    });
    return projected;
}

/** Abbreviated repeat check: operands stay on the rod while Decision flips in place to the full check's result. */
export function projectDecidedNaturalComparison(args: Parameters<typeof projectEmbeddedNaturalComparison>[0] & {
    previous?: PrimitiveWheelState;
    cameraFocus: NonNullable<PrimitiveStepState['cameraFocus']>;
}): PrimitiveStepState {
    const { config } = args;
    const full = projectEmbeddedNaturalComparison(args);
    const returned = full.at(-1)!.keyframes.at(-1)!;
    const resolved = returned.wheels.find(wheel => wheel.role === 'less-than-decision')!;
    if (resolved.valueKind !== 'boolean') throw new Error('Embedded comparison Decision must be Boolean.');
    const previous = args.previous ?? full[0]!.keyframes[0]!.wheels.find(wheel => wheel.role === 'less-than-decision')!;
    const timing = config.natural.animation?.naturalLessThan;
    const withDecision = (decision: PrimitiveWheelState) => [...args.resting, decision];
    return {
        ...momentKey({ frames: [args.frame], step: momentPart('decide') }),
        label: 'Decide Less Than',
        description: `${args.source} < ${args.target} is ${args.source < args.target}; Decision updates without repeating the visual check.`,
        cameraFocus: args.cameraFocus,
        keyframes: [
            { ...returned, wheels: withDecision({ ...previous, position: resolved.position }), transition: 'instant', events: [] },
            { ...returned, wheels: withDecision({ ...resolved, discGrowth: 0 }),
                transition: { durationMs: timing?.decisiveCellFlashDurationMs ?? 0 }, events: [] },
            { ...returned, wheels: withDecision({ ...resolved, discGrowth: 1 }),
                transition: { durationMs: timing?.decisionMaterializationDurationMs ?? 0 }, events: [] },
        ],
    };
}
